/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { css, cx } from "@panda/css";
import { Box, BoxProps } from "@panda/jsx";
import { forwardRef, Ref } from "react";

import {
	sectionPaddingBlockEndCva,
	sectionPaddingBlockStartCva,
	sectionPaddingInlineCss,
	usePaddingBlockVariant,
} from "@components/utils/section.utils";

const styled = css({
	position: "relative",
	marginInline: "auto",
});

export interface SectionContainerProps extends BoxProps {
	animate?: boolean;
	children?: React.ReactNode;
	className?: string;
	disablePaddingBlock?: boolean;
	disablePaddingBlockStart?: boolean;
	disablePaddingBlockEnd?: boolean;
	disablePaddingInline?: boolean;
}

export const SectionContainer = forwardRef(
	(
		{
			children,
			className = "",
			disablePaddingBlock = false,
			disablePaddingInline = false,
			disablePaddingBlockStart = false,
			disablePaddingBlockEnd = false,
			...rest
		}: SectionContainerProps,
		forwardedRef: Ref<HTMLDivElement>,
	): React.JSX.Element => {
		const paddingBlockVariant = usePaddingBlockVariant();
		const isBlockStart = Boolean(!disablePaddingBlock && !disablePaddingBlockStart);
		const isBlockEnd = Boolean(!disablePaddingBlock && !disablePaddingBlockEnd);

		return (
			<Box
				className={cx(
					"section-container",
					styled,
					isBlockStart && sectionPaddingBlockStartCva({ paddingBlockVariant }),
					isBlockStart && "padding-block-start",
					isBlockEnd && sectionPaddingBlockEndCva({ paddingBlockVariant }),
					isBlockEnd && "padding-block-end",
					!disablePaddingInline && sectionPaddingInlineCss,
					!disablePaddingInline && "padding-inline",
					className,
				)}
				{...rest}
				ref={forwardedRef}
			>
				{children}
			</Box>
		);
	},
);

SectionContainer.displayName = "SectionContainer";
